import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Shield, Key, Upload, Copy, Clock, AlertTriangle, CheckCircle, Smartphone, Star } from 'lucide-react';

const PRICE = 75000;

export default function LicenseScreen({ licenseInfo, onActivated }) {
  const [tab, setTab] = useState('payment');
  const [payInfo, setPayInfo] = useState(null);
  const [payForm, setPayForm] = useState({ customer_name: '', customer_phone: '', transaction_id: '', amount: String(PRICE), message: '' });
  const [screenshot, setScreenshot] = useState(null);
  const [activateKey, setActivateKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [paySubmitted, setPaySubmitted] = useState(licenseInfo?.payment_submitted || false);
  const fileRef = useRef();

  useEffect(() => {
    api.get('/license/payment-info').then(r => setPayInfo(r.data)).catch(() => {});
  }, []);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!activateKey.trim()) return toast.error('Please enter your license key');
    setLoading(true);
    try {
      const res = await api.post('/license/activate', { license_key: activateKey.trim() });
      if (res.data.success) {
        toast.success('🎉 License activated successfully!');
        setTimeout(() => onActivated(), 1000);
      } else {
        toast.error(res.data.message || 'Invalid key');
      }
    } catch { toast.error('Network error'); }
    finally { setLoading(false); }
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payForm.transaction_id) return toast.error('Transaction ID is required');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(payForm).forEach(([k, v]) => fd.append(k, v));
      if (screenshot) fd.append('screenshot', screenshot);
      const res = await api.post('/license/payment', fd);
      if (res.data.success) {
        toast.success('Payment submitted! License will be activated within 24 hours.');
        setPaySubmitted(true);
      } else {
        toast.error(res.data.message || 'Payment could not be submitted');
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Network error';
      toast.error('Error: ' + errorMsg);
    }
    finally { setLoading(false); }
  };

  const openUPI = () => {
    const upiUrl = `upi://pay?pa=lakhanrathore362@ybl&pn=Workshop%20Manager%20Pro&am=${PRICE}&cu=INR&tn=Workshop%20Manager%20License`;
    window.open(upiUrl, '_blank');
  };

  const copyUPI = () => {
    navigator.clipboard.writeText('lakhanrathore362@ybl');
    toast.success('UPI ID copied!');
  };

  const isTrial = licenseInfo?.status === 'trial';
  const daysLeft = licenseInfo?.days_left || 0;
  const hoursLeft = licenseInfo?.hours_left || 0;
  const upiUrl = `upi://pay?pa=lakhanrathore362@ybl&pn=Workshop%20Manager%20Pro&am=${PRICE}&cu=INR`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/20 rounded-2xl mb-3 border border-amber-500/30">
            {isTrial ? <Clock className="w-8 h-8 text-amber-400" /> : <Shield className="w-8 h-8 text-red-400" />}
          </div>
          <h1 className="text-2xl font-bold text-white">MOZI INNOVATORS LLP</h1>
          <p className="text-slate-400 text-sm mt-1">Wood & Furniture Management System</p>

          {isTrial && (
            <div className="mt-3 inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 text-amber-300 px-4 py-2 rounded-full text-sm">
              <Clock className="w-4 h-4" />
              Trial: {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining ({hoursLeft} hours)
            </div>
          )}
          {!isTrial && (
            <div className="mt-3 inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-2 rounded-full text-sm">
              <AlertTriangle className="w-4 h-4" />
              {licenseInfo?.status === 'expired' ? 'License Expired' : 'Trial Ended — Purchase License'}
            </div>
          )}
        </div>

        {/* Pricing Box */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-4 mb-4 text-center">
          <div className="flex items-center justify-center gap-3">
            <div>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-black text-amber-400">₹75,000</span>
                <span className="text-slate-400 text-sm">/year</span>
              </div>
              <p className="text-slate-400 text-xs mt-1">Full 1-Year License — Unlimited Workers & Projects</p>
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-3 text-xs text-slate-400">
            <span>✅ Unlimited Projects</span>
            <span>✅ Unlimited Workers</span>
            <span>✅ Full Reports</span>
          </div>
        </div>

        {/* Machine ID */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 mb-4 text-center">
          <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Your Machine ID (for support)</p>
          <p className="font-mono text-amber-400 text-xs break-all">{licenseInfo?.machine_id || 'Loading...'}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab('payment')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === 'payment' ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'}`}>
            💳 Pay & Activate
          </button>
          <button onClick={() => setTab('activate')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === 'activate' ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'}`}>
            🔑 Enter Key
          </button>
        </div>

        {/* PAYMENT TAB */}
        {tab === 'payment' && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden">
            {paySubmitted ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Payment Submitted!</h3>
                <p className="text-slate-400 text-sm">Your payment is being verified. It will be activated within 24 hours.</p>
                <p className="text-slate-500 text-xs mt-3">After activation, go to the "Enter Key" tab and enter your key.</p>
                {isTrial && <p className="text-amber-400 text-sm mt-4">Continue using the app during your trial period.</p>}
              </div>
            ) : (
              <>
                {/* UPI Payment */}
                <div className="p-5 border-b border-slate-700 bg-gradient-to-r from-purple-900/20 to-blue-900/20">
                  <p className="text-center text-sm font-semibold text-white mb-1">Send ₹{PRICE.toLocaleString('en-IN')} via UPI</p>
                  <p className="text-center text-xs text-slate-400 mb-4">UPI ID: <span className="text-amber-400 font-mono">lakhanrathore362@ybl</span></p>
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-white p-3 rounded-xl">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(upiUrl)}`}
                        alt="UPI QR"
                        className="w-40 h-40"
                      />
                    </div>
                    <div className="flex gap-2 w-full">
                      <button onClick={openUPI} className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-xl text-sm font-medium transition-colors">
                        <Smartphone className="w-4 h-4" /> Buy via UPI App
                      </button>
                      <button onClick={copyUPI} className="flex items-center justify-center gap-1 bg-slate-700 hover:bg-slate-600 text-white py-2.5 px-4 rounded-xl text-sm transition-colors">
                        <Copy className="w-4 h-4" /> Copy
                      </button>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handlePaySubmit} className="p-5 space-y-3">
                  <p className="text-sm font-semibold text-slate-300">Fill in details after payment:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Your Name</label>
                      <input value={payForm.customer_name} onChange={e => setPayForm({...payForm, customer_name: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500" placeholder="Full name" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Phone</label>
                      <input value={payForm.customer_phone} onChange={e => setPayForm({...payForm, customer_phone: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500" placeholder="Mobile" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Transaction ID / UTR Number *</label>
                    <input required value={payForm.transaction_id} onChange={e => setPayForm({...payForm, transaction_id: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500" placeholder="e.g. 403942857234" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Amount (₹)</label>
                    <input value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500" placeholder="75000" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Payment Screenshot</label>
                    <div onClick={() => fileRef.current.click()} className="w-full bg-slate-900 border border-dashed border-slate-600 rounded-xl px-3 py-3 text-sm text-slate-400 cursor-pointer hover:border-amber-500 transition-colors text-center">
                      <Upload className="w-4 h-4 inline mr-2" />
                      {screenshot ? screenshot.name : 'Upload screenshot'}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => setScreenshot(e.target.files[0])} />
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 py-3 rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
                    {loading ? 'Submitting...' : '✓ Submit Payment'}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {/* ACTIVATE TAB */}
        {tab === 'activate' && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
            <div className="text-center mb-5">
              <Key className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white">Enter License Key</h3>
              <p className="text-slate-400 text-sm mt-1">Enter the key you received after payment verification</p>
            </div>
            <form onSubmit={handleActivate} className="space-y-4">
              <input
                value={activateKey}
                onChange={e => setActivateKey(e.target.value.toUpperCase())}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-amber-400 font-mono text-base tracking-widest focus:outline-none focus:border-amber-500 text-center"
                placeholder="WM-XXXXXX-XXXXXX-XXXXXX-XXXXXX"
              />
              <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 py-3 rounded-xl font-bold transition-colors disabled:opacity-50">
                {loading ? 'Activating...' : '🔓 Activate License'}
              </button>
            </form>
            <div className="mt-4 p-3 bg-slate-900 rounded-xl text-xs text-slate-500 text-center">
              Didn't receive key? Contact on WhatsApp or submit payment
            </div>
          </div>
        )}

        {isTrial && (
          <button onClick={onActivated} className="w-full mt-3 text-slate-500 hover:text-slate-400 text-sm py-2 transition-colors">
            Continue in trial ({daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining) →
          </button>
        )}
      </div>
    </div>
  );
}
